const ESC = 0x1b;
const GS = 0x1d;

export function escPosInit() {
  return new Uint8Array([ESC, 0x40, ESC, 0x61, 0x00]);
}

export function escPosCut() {
  return new Uint8Array([GS, 0x56, 0x00]);
}

export function escPosFeed(lines = 3) {
  return new Uint8Array([ESC, 0x64, Math.min(255, lines)]);
}

export function escPosSetTextMode(mode) {
  return new Uint8Array([ESC, 0x21, mode & 0xff]);
}

function encodeTicketText(text) {
  const normalized = String(text || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[·•–—]/g, '-')
    .replace(/[^\x20-\x7E]/g, '');
  const bytes = [];
  for (let i = 0; i < normalized.length; i++) bytes.push(normalized.charCodeAt(i));
  return new Uint8Array(bytes);
}

export function concatBytes(...parts) {
  const total = parts.reduce((n, p) => n + p.length, 0);
  const out = new Uint8Array(total);
  let offset = 0;
  for (const p of parts) {
    out.set(p, offset);
    offset += p.length;
  }
  return out;
}

export function centerLine(text, width = 32) {
  const t = String(text || '').trim();
  if (t.length >= width) return t.slice(0, width);
  const pad = Math.floor((width - t.length) / 2);
  return `${' '.repeat(pad)}${t}`;
}

export function wrapText(text, width = 32) {
  const words = String(text || '').trim().split(/\s+/);
  const lines = [];
  let line = '';
  for (const w of words) {
    const next = line ? `${line} ${w}` : w;
    if (next.length <= width) line = next;
    else {
      if (line) lines.push(line);
      line = w.length > width ? w.slice(0, width) : w;
    }
  }
  if (line) lines.push(line);
  return lines.length ? lines : [''];
}

export function buildEscPosTextBody(lines, opts) {
  const feed1 = escPosFeed(1);
  const trailing = Math.max(0, Math.min(8, opts?.trailingFeed ?? 2));
  const chunks = [];
  for (const line of lines) {
    chunks.push(encodeTicketText(line), feed1);
  }
  if (trailing > 0) chunks.push(escPosFeed(trailing));
  return concatBytes(...chunks);
}

export function buildEscPosDocument(lines, fontScalePercent = 100) {
  const mode = fontScalePercent >= 130 ? 0x30 : fontScalePercent >= 110 ? 0x10 : 0x00;
  return concatBytes(
    escPosInit(),
    escPosSetTextMode(mode),
    buildEscPosTextBody(lines, { trailingFeed: 2 }),
    escPosCut()
  );
}

export function buildKitchenTicketLines(order) {
  const lines = [];
  const table = order.table_number != null ? `MESA ${order.table_number}` : null;
  const title = table || (order.customer_name ? String(order.customer_name).toUpperCase() : `PEDIDO #${order.order_number || ''}`);
  lines.push(centerLine('*** COCINA ***'));
  lines.push(centerLine(title));
  lines.push(centerLine(`#${order.order_number || ''}  ${new Date().toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}`));
  lines.push('-'.repeat(32));
  for (const ln of order.lines || []) {
    if (ln.line_status === 'draft' || ln.line_status === 'cancelled') continue;
    const size = ln.size_label ? ` (${ln.size_label})` : '';
    lines.push(`${ln.qty || 1}x ${(ln.name || '').toUpperCase()}${size}`);
    if (ln.half_specialty_name) lines.push(`  Mitad: ${ln.half_specialty_name}`);
    const extras = (ln.extra_toppings || []).map((e) => e.name).filter(Boolean);
    if (extras.length) lines.push(`  Extra: ${extras.join(', ')}`);
    if (ln.kitchen_note) lines.push(`  * ${ln.kitchen_note}`);
  }
  lines.push('-'.repeat(32));
  return lines;
}

export function buildCashierTicketLines(order) {
  const lines = [];
  lines.push(centerLine('*** CUENTA ***'));
  lines.push(centerLine(order.table_number != null ? `Mesa ${order.table_number}` : `#${order.order_number || ''}`));
  if (order.customer_name) lines.push(centerLine(String(order.customer_name)));
  lines.push('-'.repeat(32));
  for (const ln of order.lines || []) {
    if (ln.line_status === 'cancelled') continue;
    const total = Number(ln.line_total || 0).toFixed(2);
    const size = ln.size_label ? ` · ${ln.size_label}` : '';
    lines.push(`${ln.qty || 1}x ${ln.name || ''}${size}`.slice(0, 22).padEnd(22) + `$${total}`.padStart(10));
    if (ln.half_specialty_name) lines.push(`  Mitad: ${ln.half_specialty_name}`);
    const extras = (ln.extra_toppings || []).map((e) => e.name).filter(Boolean);
    if (extras.length) lines.push(`  Extra: ${extras.join(', ')}`);
  }
  lines.push('-'.repeat(32));
  if (order.auto_discount) lines.push(`Desc.`.padEnd(22) + `-$${Number(order.auto_discount).toFixed(2)}`.padStart(10));
  lines.push(`TOTAL`.padEnd(22) + `$${Number(order.total || 0).toFixed(2)}`.padStart(10));
  lines.push(centerLine('Gracias'));
  return lines;
}

/** Ticket de venta (GET /admin/orders/:id/receipt) en lineas ESC/POS de 32 cols. */
export function buildReceiptTicketLines(r) {
  const W = 32;
  const m = (n) => `$${Number(n || 0).toFixed(2)}`;
  const row = (left, right) => {
    const rt = String(right);
    return String(left).slice(0, W - rt.length - 1).padEnd(W - rt.length) + rt;
  };
  const lines = [];
  for (const h of r.header_lines || []) lines.push(...wrapText(h, W).map((x) => centerLine(x, W)));
  lines.push('-'.repeat(W));
  if (r.datetime_label) lines.push(`Fecha: ${r.datetime_label}`);
  if (r.ticket_label) lines.push(String(r.ticket_label));
  if (r.cashier_label) lines.push(`Cajero: ${r.cashier_label}`);
  if (r.register_label) lines.push(`Caja: ${r.register_label}`);
  if (r.customer_label) lines.push(`Cliente: ${r.customer_label}`);
  if (r.seller_label) lines.push(`Vendedor: ${r.seller_label}`);
  lines.push('-'.repeat(W));
  for (const ln of r.lines || []) {
    lines.push(...wrapText(ln.description, W));
    lines.push(row(`  ${ln.qty} ${ln.unit_label || 'Pza'} x ${m(ln.unit_price)}`, m(ln.line_total)));
  }
  lines.push('='.repeat(W));
  if (Number(r.delivery_fee) > 0) lines.push(row('Envio', m(r.delivery_fee)));
  if (Number(r.promo_discount) > 0) lines.push(row('Descuento', `-${m(r.promo_discount)}`));
  lines.push(row('TOTAL', m(r.total)));
  if (r.amount_words) lines.push(...wrapText(r.amount_words, W));
  lines.push('-'.repeat(W));
  lines.push(row(r.payment_method_label || 'Pago', m(r.total)));
  lines.push('-'.repeat(W));
  for (const f of r.footer_lines || []) lines.push(...wrapText(f, W).map((x) => centerLine(x, W)));
  return lines;
}